import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { Progress } from '@/components/ui/progress'
import { ChecklistSignatureDialog } from '@/components/jobs/checklist-signature-dialog'
import { getErrorMessage } from '@/lib/api'
import { checklistApi } from '@/services/checklist'
import type { JobChecklistItem } from '@/types'

const isItemComplete = (item: JobChecklistItem) => item.status === 'completed' || item.status === 'skipped'

const itemNeedsSignatureBeforeComplete = (item: JobChecklistItem) =>
  item.requires_signature === true && item.has_signature !== true

type ChecklistCompletionDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  jobId: string
  workDayId?: string | number
  kind?: 'job' | 'project' | 'event'
  title: string
  description: string
  onConfirmed: () => void
  confirming?: boolean
}

export function ChecklistCompletionDialog({
  open,
  onOpenChange,
  jobId,
  workDayId,
  kind = 'job',
  title,
  description,
  onConfirmed,
  confirming = false,
}: ChecklistCompletionDialogProps) {
  const queryClient = useQueryClient()
  const queryKey = ['job-checklist', jobId, workDayId ?? 'job']
  const [localItems, setLocalItems] = useState<JobChecklistItem[]>([])
  const [signatureItem, setSignatureItem] = useState<JobChecklistItem | null>(null)

  const checklistQuery = useQuery({
    queryKey,
    queryFn: () => checklistApi.getJobChecklist(jobId, workDayId, kind),
    enabled: open && Boolean(jobId),
  })

  useEffect(() => {
    if (checklistQuery.data?.items) {
      setLocalItems(checklistQuery.data.items)
    }
  }, [checklistQuery.data?.items])

  const summary = useMemo(() => {
    const requiredItems = localItems.filter((item) => item.is_required)
    const requiredCompleted = requiredItems.filter(isItemComplete).length
    const signatureItems = localItems.filter((item) => item.requires_signature)
    const signaturesCaptured = signatureItems.filter((item) => item.has_signature === true).length
    return {
      required_total: requiredItems.length,
      required_completed: requiredCompleted,
      all_required_complete: requiredItems.length === 0 || requiredCompleted === requiredItems.length,
      signature_total: signatureItems.length,
      signatures_captured: signaturesCaptured,
      all_signatures_captured: signatureItems.length === 0 || signaturesCaptured === signatureItems.length,
    }
  }, [localItems])

  const toggleMutation = useMutation({
    mutationFn: async ({
      item,
      checked,
      signatureFile,
      customerName,
    }: {
      item: JobChecklistItem
      checked: boolean
      signatureFile?: File
      customerName?: string
    }) => {
      if (checked) {
        return checklistApi.completeItem(item.id, {
          signatureFile,
          customerName,
        })
      }
      return checklistApi.reopenItem(item.id)
    },
    onSuccess: (updatedItem, variables) => {
      if (variables.checked && variables.signatureFile) {
        setSignatureItem(null)
      }
      setLocalItems((current) => current.map((item) => (item.id === updatedItem.id ? updatedItem : item)))
      queryClient.invalidateQueries({ queryKey })
      queryClient.invalidateQueries({ queryKey: ['job', jobId] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const handleCompletionToggle = (item: JobChecklistItem, checked: boolean) => {
    if (checked && itemNeedsSignatureBeforeComplete(item)) {
      setSignatureItem(item)
      return
    }

    toggleMutation.mutate({ item, checked })
  }

  const progressValue = summary.required_total > 0
    ? Math.round((summary.required_completed / summary.required_total) * 100)
    : 100

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>

        {checklistQuery.isLoading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading checklist...
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                {summary.required_completed} of {summary.required_total} required steps complete
              </p>
              <Progress value={progressValue} className="h-2" />
              {summary.signature_total > 0 ? (
                <p className="text-xs text-purple-700">
                  {summary.signatures_captured} of {summary.signature_total} required signatures captured
                </p>
              ) : null}
            </div>

            {localItems.length === 0 ? (
              <p className="text-sm text-muted-foreground">No checklist items for this step.</p>
            ) : (
              <ul className="max-h-64 space-y-2 overflow-y-auto pr-1">
                {localItems.map((item) => {
                  const complete = isItemComplete(item)
                  const pending = toggleMutation.isPending && toggleMutation.variables?.item.id === item.id

                  return (
                    <li
                      key={item.id}
                      className="flex items-start gap-3 rounded-md border border-border/60 px-3 py-2"
                    >
                      <Checkbox
                        checked={complete}
                        disabled={pending}
                        onCheckedChange={(checked) => {
                          handleCompletionToggle(item, checked === true)
                        }}
                        className="mt-0.5"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm">{item.title}</p>
                        {item.is_required ? (
                          <p className="text-xs text-amber-700">Required</p>
                        ) : (
                          <p className="text-xs text-muted-foreground">Optional</p>
                        )}
                        {item.requires_signature ? (
                          <p className="text-xs text-purple-700">
                            Signature required{item.has_signature ? ' · captured' : ''}
                          </p>
                        ) : null}
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={confirming || toggleMutation.isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={
              confirming
              || toggleMutation.isPending
              || checklistQuery.isLoading
              || !summary.all_required_complete
              || !summary.all_signatures_captured
            }
            onClick={(event) => {
              event.preventDefault()
              onConfirmed()
            }}
          >
            {confirming ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Confirm and continue
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>

      <ChecklistSignatureDialog
        open={Boolean(signatureItem)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && !toggleMutation.isPending) {
            setSignatureItem(null)
          }
        }}
        itemTitle={signatureItem?.title ?? 'Checklist step'}
        isPending={toggleMutation.isPending}
        onSubmit={({ signatureFile, customerName }) => {
          if (!signatureItem) {
            return
          }

          toggleMutation.mutate({
            item: signatureItem,
            checked: true,
            signatureFile,
            customerName,
          })
        }}
      />
    </AlertDialog>
  )
}
